import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, Building2, Handshake, KeyRound, LineChart, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import SearchBar from "@/components/SearchBar";
import PropertyCard from "@/components/PropertyCard";
import { Reveal, MaskedLine, Eyebrow } from "@/components/Reveal";
import { HERO_IMG, CATEGORY_IMAGES } from "@/lib/images";
import { formatPrice } from "@/lib/format";

const SERVICES = [
  { icon: Handshake, title: "وساطة بيع", desc: "تسويق احترافي لعقارك والتفاوض نيابة عنك حتى الإفراغ." },
  { icon: KeyRound, title: "تأجير وإدارة أملاك", desc: "إدارة كاملة للعقارات المؤجرة من التسويق إلى التحصيل." },
  { icon: Building2, title: "تسويق مشاريع", desc: "خطط تسويقية للمشاريع السكنية على الخارطة والجاهزة." },
  { icon: LineChart, title: "استشارات عقارية", desc: "دراسات سعر وموقع تساعدك على قرار شراء أو استثمار سليم." },
];

const STATS = [
  { value: "+120", label: "عقار افتراضي معروض" },
  { value: "3", label: "مدن رئيسية" },
  { value: "8", label: "مشاريع نموذجية" },
  { value: "98%", label: "رضا عملاء (توضيحي)" },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [projects, setProjects] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  useEffect(() => {
    api.get("/properties", { params: { featured: true } }).then((r) => setFeatured(r.data.items.slice(0, 5))).catch(() => {});
    api.get("/projects").then((r) => setProjects(r.data.items.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <>
      <Seo description="نموذج تجريبي لموقع وساطة عقارية فاخر — عقارات ومشاريع افتراضية في جدة والرياض والدمام لأغراض العرض." />

      <section ref={heroRef} className="relative min-h-[92vh] flex items-end overflow-hidden" data-testid="hero-section">
        <motion.div className="absolute inset-0" style={{ y: bgY }}>
          <img src={HERO_IMG} alt="فيلا فاخرة بتصميم معماري سعودي معاصر" className="h-[115%] w-full object-cover" />
        </motion.div>
        <div className="absolute inset-0 bg-black/45" aria-hidden="true" />
        <div className="relative container-lux w-full pb-16 pt-40 space-y-8">
          <p className="text-gold font-heading font-semibold tracking-widest text-sm" data-testid="hero-tagline">
            تجربة لموقع وساطة عقارية متكامل
          </p>
          <h1 className="text-ivory font-heading font-extrabold text-4xl sm:text-5xl lg:text-7xl leading-[1.15] tracking-tight max-w-3xl">
            <MaskedLine delay={0.15}>عقاراتٌ فاخرة</MaskedLine>
            <MaskedLine delay={0.35}>
              بلمسةٍ <span className="text-gold">سعوديةٍ معاصرة</span>
            </MaskedLine>
          </h1>
          <MaskedLine delay={0.55} className="text-ivory/85 text-base md:text-lg max-w-xl leading-relaxed">
            استكشف مجموعة مختارة من العقارات والمشاريع في جدة والرياض والدمام — عبر تجربة تصفح صُممت لتليق بطموحك.
          </MaskedLine>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl"
            data-testid="hero-search"
          >
            <SearchBar />
          </motion.div>
        </div>
        <motion.div
          className="absolute bottom-6 left-1/2 -translate-x-1/2 text-ivory/70"
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          aria-hidden="true"
        >
          <ChevronDown className="h-6 w-6" />
        </motion.div>
      </section>

      <section className="container-lux py-20 md:py-32" data-testid="featured-section">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div className="space-y-3">
            <Eyebrow>مختارات مميزة</Eyebrow>
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-forest tracking-tight">عقارات تستحق المشاهدة</h2>
          </div>
          <Link to="/properties" className="btn-outline-forest text-sm" data-testid="featured-view-all">
            جميع العقارات
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          {featured.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.08} className={i === 0 ? "md:col-span-7" : i === 1 ? "md:col-span-5" : "md:col-span-4"}>
              <PropertyCard property={p} large={i === 0} />
            </Reveal>
          ))}
          {featured.length === 0 && (
            <div className="md:col-span-12 grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1, 2, 3].map((n) => (
                <div key={n} className="h-72 animate-pulse rounded-2xl bg-forest/5" />
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="bg-white py-20 md:py-28" data-testid="categories-section">
        <div className="container-lux">
          <div className="space-y-3 mb-12">
            <Eyebrow>تصفح حسب النوع</Eyebrow>
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-forest tracking-tight">ماذا تبحث عنه؟</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {Object.entries(CATEGORY_IMAGES).map(([label, img], i) => (
              <Reveal key={label} delay={i * 0.07}>
                <Link
                  to={`/properties?ptype=${encodeURIComponent(label)}`}
                  className="group relative block h-64 md:h-80 overflow-hidden rounded-2xl"
                  data-testid={`category-card-${label}`}
                >
                  <img src={img} alt={`عقارات من نوع ${label}`} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-forest-dark/80 via-forest/20 to-transparent" aria-hidden="true" />
                  <div className="absolute bottom-5 start-5 text-ivory">
                    <p className="font-heading font-bold text-xl">{label === "أرض" ? "أراضٍ" : label === "فيلا" ? "فلل" : label === "شقة" ? "شقق" : "مكاتب"}</p>
                    <p className="text-xs text-gold mt-1 flex items-center gap-1 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                      استكشف الآن <ArrowLeft className="h-3 w-3" aria-hidden="true" />
                    </p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="container-lux py-20 md:py-32" data-testid="projects-section">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div className="space-y-3">
            <Eyebrow>مشاريع سكنية</Eyebrow>
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-forest tracking-tight">مجمعات بروحٍ جديدة</h2>
          </div>
          <Link to="/projects" className="btn-outline-forest text-sm" data-testid="projects-view-all">
            جميع المشاريع
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {projects.map((pr, i) => (
            <Reveal key={pr.id} delay={i * 0.08}>
              <Link to={`/projects/${pr.id}`} className="card-lux group block overflow-hidden rounded-2xl" data-testid={`project-card-${pr.id}`}>
                <div className="relative h-64 overflow-hidden">
                  <img src={pr.images?.[0]} alt={pr.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <span className="absolute top-4 start-4 rounded-full bg-gold px-4 py-1.5 text-xs font-heading font-bold text-forest">{pr.status}</span>
                </div>
                <div className="p-6 space-y-3">
                  <h3 className="font-heading font-bold text-xl text-forest">{pr.name}</h3>
                  <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">{pr.description}</p>
                  {pr.starting_price && (
                    <p className="text-sm font-heading font-bold text-gold-dark">تبدأ من {formatPrice(pr.starting_price)}</p>
                  )}
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-forest py-20 md:py-28" data-testid="services-section">
        <div className="container-lux">
          <div className="space-y-3 mb-14 max-w-xl">
            <p className="text-gold text-sm font-heading font-semibold tracking-widest flex items-center gap-3">
              <span className="inline-block h-px w-8 bg-gold" /> خدماتنا
            </p>
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-ivory tracking-tight">وساطة متكاملة من البحث حتى الإفراغ</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {SERVICES.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08}>
                <div className="group rounded-2xl border border-ivory/10 bg-forest-light/40 p-7 transition-colors duration-300 hover:border-gold/40" data-testid={`service-card-${i}`}>
                  <s.icon className="h-8 w-8 text-gold mb-5 transition-transform duration-300 group-hover:-translate-y-1" aria-hidden="true" />
                  <h3 className="font-heading font-bold text-lg text-ivory mb-2">{s.title}</h3>
                  <p className="text-sm text-ivory/70 leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-6 border-t border-ivory/10 pt-12" data-testid="stats-strip">
            {STATS.map((s) => (
              <div key={s.label}>
                <p className="font-heading font-extrabold text-3xl md:text-4xl text-gold">{s.value}</p>
                <p className="text-sm text-ivory/70 mt-1">{s.label}</p>
              </div>
            ))}
          </div>
          <p className="mt-4 text-[11px] text-ivory/40">أرقام توضيحية افتراضية لأغراض العرض التقديمي فقط.</p>
        </div>
      </section>

      <section className="container-lux py-20 md:py-28" data-testid="cta-request-section">
        <div className="relative overflow-hidden rounded-3xl bg-forest px-8 py-16 md:px-16 md:py-20">
          <div className="absolute -top-20 -end-20 h-64 w-64 rounded-full border-[30px] border-gold/15" aria-hidden="true" />
          <div className="relative max-w-2xl space-y-6">
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-ivory tracking-tight leading-snug">لم تجد ما يناسبك؟</h2>
            <p className="text-ivory/75 leading-relaxed">
              أخبرنا بمواصفات عقارك المثالي — المدينة، النوع، والميزانية — وسيقوم فريقنا بترشيح خيارات تناسبك.
            </p>
            <Link to="/request" className="btn-gold" data-testid="cta-request-button">
              اطلب عقارك الآن
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
